<template>

<li
  :key="navigation.name"
  @click.stop="handleItemClick()"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave"
  :class="[{ 'active': navigation.active }]"
  v-if="canShow"
>
  <router-link :to="(navigation.children && navigation.children.length > 0) ? '#' : (navigation.path || '#')">
    <i class="fa" :class="navigation.icon"></i>

    <span v-if="!sublevel" class="nav-label">{{ navigation.name }}</span>
    <template v-else>{{ navigation.name }}</template>

    <span class="fa arrow" v-if="navigation.children"></span>
  </router-link>

  <ul
    class="nav nav-second-level collapse"
    v-if="navigation.children && navigation.children.length > 0"
    :class="[(navigation.active || isHovered) ? 'in': '']"
  >
    <SidebarNavigation
      v-for="(child, key) in navigation.children"
      :key="key"
      :navigation="child"
      :menu="menu"
      :sublevel="true"
      :user="user"
    />
  </ul>
</li>

</template>

<script>
export default {
  name: 'SidebarNavigation',
  props: {
    navigation: {
      type: Object,
      required: true
    },
    menu: {
      type: Array,
      required: true
    },
    sublevel: {
      type: Boolean,
      required: false
    },
    user: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      isHovered: false
    };
  },
  computed: {
    canShow () {
      const { admin, module } = this.navigation
      if (admin) {
        return this.user.admin
      }
      if (module && !this.user.systemmodules.includes(module)) {
        return this.user.admin || ((process.env.VUE_APP_SHOW_SYS_MODULE_MENU * 1) === 1)
      }
      return true
    }
  },
  methods: {
    handleItemClick() {
      // Se o item tem filhos, o clique alterna o estado 'active' (expansão persistente)
      if (this.navigation.children && this.navigation.children.length > 0) {
        this.navigation.active = !this.navigation.active;
        // O this.$forceUpdate() abaixo pode não ser necessário se a reatividade estiver configurada corretamente.
        // Se o Vue não estiver detectando a mudança em `navigation.active` (que é parte de uma prop),
        // pode ser necessário investigar a reatividade do array `menu` no componente pai.
        // this.$forceUpdate();
      } else if (this.navigation.path) {
        // Para itens finais (sem filhos), a navegação é primariamente feita pelo router-link.
        // Esta parte é um fallback se o clique ocorrer no <li> fora do <a>.
        // Como o router-link para itens pais agora é '#', esta condição não será
        // atingida para eles através do clique no <a> do item pai.
        this.$router.push({ path: this.navigation.path });
      }
    },
    handleMouseEnter() {
      if (this.navigation.children && this.navigation.children.length > 0) {
        this.isHovered = true;
      }
    },
    handleMouseLeave() {
      if (this.navigation.children && this.navigation.children.length > 0) {
        this.isHovered = false;
      }
    },

    // Função auxiliar recursiva: define o estado 'active' para um item e seus filhos.
    // Retorna true se o item atual ou um de seus descendentes corresponde à rota atual.
    _setActiveStateRecursive(item, currentPath) {
      let isChildActive = false;
      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          // Chama recursivamente para cada filho. Se algum filho estiver ativo,
          // marca que um filho deste item 'item' está ativo.
          if (this._setActiveStateRecursive(child, currentPath)) {
            isChildActive = true;
          }
        }
      }

      // Um item é considerado 'active' se:
      // 1. Seu 'path' corresponde à 'currentPath' E ele é um item final (não tem filhos OU seu path é o alvo direto).
      // OU 2. Um de seus 'children' está ativo.
      const isActive = (item.path === currentPath && (!item.children || item.children.length === 0)) || isChildActive;

      // Modifica 'item.active' apenas se o novo estado for diferente do atual.
      // Isso pode ajudar a evitar re-renderizações desnecessárias se a reatividade estiver configurada corretamente.
      if (item.active !== isActive) {
        // Idealmente, Vue.set(item, 'active', isActive) seria usado se 'active'
        // não fosse uma propriedade preexistente ou para garantir reatividade.
        // Como 'active' já existe nos seus objetos de menu, a atribuição direta
        // pode funcionar, mas o $forceUpdate() sugere problemas de reatividade subjacentes.
        item.active = isActive;
      }
      return isActive; // Retorna se este item (ou um descendente) está ativo.
    },

    // Função principal para atualizar o estado 'active' de todo o menu.
    // Chamada no 'created' e no 'watch $route'.
    _updateFullMenuState() {
      const currentPath = this.$route.path;
      // Itera sobre cada item de menu de nível superior e chama a função recursiva.
      this.menu.forEach(topLevelItem => {
        this._setActiveStateRecursive(topLevelItem, currentPath);
      });

      // O $forceUpdate() é mantido aqui como um paliativo. Idealmente, as modificações
      // em 'item.active' deveriam ser detectadas pelo Vue automaticamente se o array 'menu'
      // e seus objetos fossem totalmente reativos e gerenciados de forma a respeitar
      // as regras de reatividade do Vue (ex: não modificar props diretamente, usar Vue.set ou computed props).
      this.$forceUpdate();
    }
  },
  created () {
    this._updateFullMenuState();
  },
  watch: {
    $route: function () { // Não precisamos do parâmetro 'val' se não o usamos
      this._updateFullMenuState();
    }
  }
}
</script>
